import type {
  AuthStatus,
  User,
  Exercise,
  Workout,
  CreateWorkoutInput,
  WorkoutsListResponse,
  WorkoutExercise,
  WorkoutStats,
  ExerciseProgressResponse,
  WorkoutFrequencyResponse,
  UserExercisesResponse,
  CardioProgressResponse,
  CardioActivityType,
  StatsPeriod,
  StatsGroupBy,
  WearableProvider,
  WearableConnection,
  DailyHealthSummary,
  WearableActivity,
  HealthTrendsResponse,
  HealthPeriod,
  SyncResponse,
  WorkoutLinkSuggestion,
  TrainingProgram,
  ProgramTemplate,
  ProgramExercise,
  CreateProgramInput,
  UpdateProgramInput,
  CreateTemplateInput,
  UpdateTemplateInput,
  AddProgramExerciseInput,
  StartWorkoutInput,
  LastWeightsResponse,
  PreviousSetsResponse,
  ExerciseSettingsResponse,
  MonthlySummaryResponse,
  VolumeWeek,
  CalendarDay,
  ExerciseStatsResponse,
  StatsMonths,
  FilterWorkoutType,
  WeightEntry,
  WeightEntryInput,
  WeightUpdateInput,
  WeightStatsResponse,
  WeightPeriod,
  UserProfile,
  ProfileUpdateInput,
  BMIResponse,
  SquatProgram,
  SquatSession,
  SquatSet,
  SquatProgramWeekDef,
  SquatWeekWithWeights,
  SquatSessionStats,
  SquatCompleteResponse,
} from '@/types';

const API_BASE = '/api';

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  // Älä lähetä Content-Type headeria DELETE-pyynnöissä ilman bodya
  // iPhone Safari lähettää muuten tyhjän bodyn, jonka Fastify hylkää
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  // Lisää Content-Type vain jos on body
  if (options.body) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new ApiError(
      response.status,
      error.message || `API error: ${response.status}`
    );
  }

  return response.json();
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export const authApi = {
  getStatus: () => fetchApi<AuthStatus>('/auth/status'),
  getMe: () => fetchApi<{ user: User }>('/auth/me'),
  logout: () => fetchApi<{ success: boolean }>('/auth/logout', { method: 'POST' }),
  getGoogleLoginUrl: () => `${API_BASE}/auth/google`,
};

export const workoutsApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
    workoutType?: FilterWorkoutType;
  }) => {
    const searchParams = new URLSearchParams();
    const limit = params?.limit || 10;
    const page = params?.page || 1;
    const offset = (page - 1) * limit;

    searchParams.set('limit', limit.toString());
    searchParams.set('offset', offset.toString());
    if (params?.startDate) searchParams.set('startDate', params.startDate);
    if (params?.endDate) searchParams.set('endDate', params.endDate);
    if (params?.workoutType) searchParams.set('workoutType', params.workoutType);

    const query = searchParams.toString();
    return fetchApi<WorkoutsListResponse>(`/workouts${query ? `?${query}` : ''}`);
  },

  get: (id: string) => fetchApi<Workout>(`/workouts/${id}`),

  create: (data: CreateWorkoutInput) =>
    fetchApi<Workout>('/workouts', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<CreateWorkoutInput>) =>
    fetchApi<Workout>(`/workouts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    fetchApi<{ success: boolean }>(`/workouts/${id}`, { method: 'DELETE' }),

  complete: (id: string) =>
    fetchApi<Workout>(`/workouts/${id}/complete`, { method: 'POST' }),

  addExercise: (workoutId: string, exerciseId: string, order?: number) =>
    fetchApi<WorkoutExercise>(`/workouts/${workoutId}/exercises`, {
      method: 'POST',
      body: JSON.stringify({ exerciseId, order }),
    }),

  removeExercise: (workoutId: string, workoutExerciseId: string) =>
    fetchApi<{ success: boolean }>(`/workouts/${workoutId}/exercises/${workoutExerciseId}`, {
      method: 'DELETE',
    }),

  addSet: (workoutId: string, workoutExerciseId: string, data: {
    setNumber: number;
    reps?: number;
    weight?: number;
    rpe?: number;
    notes?: string;
  }) =>
    fetchApi<{ id: string }>(`/workouts/${workoutId}/exercises/${workoutExerciseId}/sets`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateSet: (workoutId: string, workoutExerciseId: string, setId: string, data: {
    setNumber?: number;
    reps?: number;
    weight?: number;
    rpe?: number;
    notes?: string;
  }) =>
    fetchApi<{ id: string }>(`/workouts/${workoutId}/exercises/${workoutExerciseId}/sets/${setId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteSet: (workoutId: string, workoutExerciseId: string, setId: string) =>
    fetchApi<{ success: boolean }>(`/workouts/${workoutId}/exercises/${workoutExerciseId}/sets/${setId}`, {
      method: 'DELETE',
    }),

  getStats: () => fetchApi<WorkoutStats>('/workouts/stats/summary'),
};

export const exercisesApi = {
  list: (params?: {
    muscleGroup?: string;
    exerciseType?: string;
    search?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.muscleGroup) searchParams.set('muscleGroup', params.muscleGroup);
    if (params?.exerciseType) searchParams.set('exerciseType', params.exerciseType);
    if (params?.search) searchParams.set('search', params.search);

    const query = searchParams.toString();
    return fetchApi<{
      exercises: Exercise[];
      grouped: Record<string, Exercise[]>;
      total: number;
    }>(`/exercises${query ? `?${query}` : ''}`);
  },

  get: (id: string) => fetchApi<Exercise>(`/exercises/${id}`),

  create: (data: { name: string; muscleGroup?: string; exerciseType?: string }) =>
    fetchApi<Exercise>('/exercises', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<{ name: string; muscleGroup?: string; exerciseType?: string }>) =>
    fetchApi<Exercise>(`/exercises/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    fetchApi<{ success: boolean }>(`/exercises/${id}`, { method: 'DELETE' }),

  getLastWeights: (id: string) =>
    fetchApi<LastWeightsResponse>(`/exercises/${id}/last-weights`),

  // Edellisen kerran sarjat ghost-esitäyttöä varten (osio 6.2).
  // 404 (ei historiaa) palautetaan null:na, ei virheenä.
  getPreviousSets: async (id: string): Promise<PreviousSetsResponse | null> => {
    try {
      return await fetchApi<PreviousSetsResponse>(`/exercises/${id}/previous-sets`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }
  },

  // Liikekohtainen lepoaika (osio 6.4)
  getSettings: (id: string) =>
    fetchApi<ExerciseSettingsResponse>(`/exercises/${id}/settings`),

  // PUT palauttaa 204 (ei bodya) → ei käytetä fetchApi:a joka jäsentäisi JSONin.
  putSettings: async (id: string, restSeconds: number): Promise<void> => {
    const response = await fetch(`${API_BASE}/exercises/${id}/settings`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ restSeconds }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new ApiError(response.status, error.message || 'Lepoajan tallennus epäonnistui');
    }
  },
};

export const statsApi = {
  // Hae dashboard-yhteenveto
  getSummary: () =>
    fetchApi<import('../types').StatsSummaryResponse>('/stats/summary'),

  // Hae painonkehitys tietylle liikkeelle
  getExerciseProgress: (exerciseId: string, period: StatsPeriod = '3m') =>
    fetchApi<ExerciseProgressResponse>(
      `/stats/${exerciseId}/progress?period=${period}`
    ),

  // Hae treenimäärät aikajaksottain
  getWorkoutFrequency: (groupBy: StatsGroupBy = 'week', period: StatsPeriod = '3m') =>
    fetchApi<WorkoutFrequencyResponse>(
      `/stats/workout-frequency?groupBy=${groupBy}&period=${period}`
    ),

  // Hae käyttäjän tekemät liikkeet (dropdown-valikkoa varten)
  getUserExercises: () =>
    fetchApi<UserExercisesResponse>('/stats/user-exercises'),

  // Hae cardio-treenien tilastot (Garmin-datasta)
  getCardioProgress: (period: StatsPeriod = '3m', activityType: CardioActivityType = 'all') =>
    fetchApi<CardioProgressResponse>(
      `/stats/cardio-progress?period=${period}&activityType=${activityType}`
    ),

  // ---- Osio 8: uudet endpointit ----

  // Kuukausikooste (year 2000–2100, month 1–12)
  getMonthlySummary: (year: number, month: number) =>
    fetchApi<MonthlySummaryResponse>(`/stats/monthly-summary?year=${year}&month=${month}`),

  // Viikkovolyymi (months = 3 | 6 | 12)
  getVolume: (months: StatsMonths) =>
    fetchApi<VolumeWeek[]>(`/stats/volume?months=${months}`),

  // Treenikalenteri (year, month)
  getCalendar: (year: number, month: number) =>
    fetchApi<CalendarDay[]>(`/stats/calendar?year=${year}&month=${month}`),

  // Liikekohtaiset kehityskäyrät (months = 3 | 6 | 12)
  getExerciseStats: (exerciseId: string, months: StatsMonths) =>
    fetchApi<ExerciseStatsResponse>(`/stats/exercise/${exerciseId}?months=${months}`),
};

// ============================================
// Wearable API (Garmin Integration)
// ============================================

interface ConnectResponse {
  needsMfa?: boolean;
  message?: string;
  id?: string;
  provider?: WearableProvider;
  username?: string;
  syncStatus?: string;
}

export const wearableApi = {
  // Hae tuetut palveluntarjoajat
  getProviders: () => fetchApi<WearableProvider[]>('/wearable/providers'),

  // Hae käyttäjän nykyinen yhteys
  getConnection: () => fetchApi<WearableConnection | null>('/wearable/connection'),

  // Yhdistä Garminiin (voi palauttaa needsMfa: true)
  connect: async (data: { username: string; password: string }): Promise<ConnectResponse> => {
    const url = `${API_BASE}/wearable/connect`;
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    // 202 = MFA required
    if (response.status === 202) {
      return result;
    }

    if (!response.ok) {
      throw new ApiError(response.status, result.message || 'Connection failed');
    }

    return result;
  },

  // Vahvista MFA-koodi
  verifyMfa: async (code: string): Promise<ConnectResponse> => {
    const url = `${API_BASE}/wearable/verify-mfa`;
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new ApiError(response.status, result.message || 'MFA verification failed');
    }

    return result;
  },

  // Poista yhteys
  disconnect: () =>
    fetchApi<{ success: boolean; message: string }>('/wearable/disconnect', {
      method: 'DELETE',
    }),

  // Manuaalinen synkronointi
  sync: () =>
    fetchApi<SyncResponse>('/wearable/sync', {
      method: 'POST',
    }),

  // Päivitä asetukset
  updateSettings: (data: { syncEnabled?: boolean }) =>
    fetchApi<{ syncEnabled: boolean }>('/wearable/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
};

// ============================================
// Health API (Terveysdata)
// ============================================

export const healthApi = {
  // Hae päivän terveysyhteenveto
  getDaily: (date: string) =>
    fetchApi<DailyHealthSummary>(`/health/daily?date=${date}`),

  // Hae terveysdata aikaväliltä
  getRange: (from: string, to: string) =>
    fetchApi<DailyHealthSummary[]>(`/health/range?from=${from}&to=${to}`),

  // Hae päivän aktiviteetit
  getActivities: (date: string) =>
    fetchApi<WearableActivity[]>(`/health/activities?date=${date}`),

  // Hae linkittämättömät aktiviteetit
  getUnlinked: () =>
    fetchApi<WearableActivity[]>('/health/activities/unlinked'),

  // Hae linkitysehdotukset aktiviteetille
  getLinkSuggestions: (activityId: string) =>
    fetchApi<WorkoutLinkSuggestion[]>(`/health/activities/${activityId}/suggestions`),

  // Linkitä aktiviteetti treeniin
  linkActivity: (activityId: string, workoutId: string) =>
    fetchApi<{ success: boolean }>(`/health/activities/${activityId}/link`, {
      method: 'POST',
      body: JSON.stringify({ workoutId }),
    }),

  // Poista aktiviteetin linkitys
  unlinkActivity: (activityId: string) =>
    fetchApi<{ success: boolean }>(`/health/activities/${activityId}/link`, {
      method: 'DELETE',
    }),

  // Hae trendidata graafeille
  getTrends: (period: HealthPeriod = 'month') =>
    fetchApi<HealthTrendsResponse>(`/health/trends?period=${period}`),
};

// ============================================
// Training Programs API (Treeniohjelmat)
// ============================================

export const programsApi = {
  // Ohjelmat
  list: () =>
    fetchApi<{ programs: TrainingProgram[] }>('/programs'),

  get: (id: string) =>
    fetchApi<TrainingProgram>(`/programs/${id}`),

  create: (data: CreateProgramInput) =>
    fetchApi<TrainingProgram>('/programs', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: UpdateProgramInput) =>
    fetchApi<TrainingProgram>(`/programs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    fetchApi<{ success: boolean }>(`/programs/${id}`, { method: 'DELETE' }),

  // Treenipohjat
  getTemplate: (programId: string, templateId: string) =>
    fetchApi<ProgramTemplate>(`/programs/${programId}/templates/${templateId}`),

  createTemplate: (programId: string, data: CreateTemplateInput) =>
    fetchApi<ProgramTemplate>(`/programs/${programId}/templates`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateTemplate: (programId: string, templateId: string, data: UpdateTemplateInput) =>
    fetchApi<ProgramTemplate>(`/programs/${programId}/templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteTemplate: (programId: string, templateId: string) =>
    fetchApi<{ success: boolean }>(`/programs/${programId}/templates/${templateId}`, {
      method: 'DELETE',
    }),

  // Pohjan liikkeet
  addExercise: (programId: string, templateId: string, data: AddProgramExerciseInput) =>
    fetchApi<ProgramExercise>(`/programs/${programId}/templates/${templateId}/exercises`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  removeExercise: (programId: string, templateId: string, exerciseId: string) =>
    fetchApi<{ success: boolean }>(
      `/programs/${programId}/templates/${templateId}/exercises/${exerciseId}`,
      { method: 'DELETE' }
    ),

  // Pohjan sarjat
  addSet: (programId: string, templateId: string, exerciseId: string, data: { setNumber: number; targetReps?: number; notes?: string }) =>
    fetchApi<{ id: string }>(`/programs/${programId}/templates/${templateId}/exercises/${exerciseId}/sets`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  deleteSet: (programId: string, templateId: string, exerciseId: string, setId: string) =>
    fetchApi<{ success: boolean }>(
      `/programs/${programId}/templates/${templateId}/exercises/${exerciseId}/sets/${setId}`,
      { method: 'DELETE' }
    ),

  // Aloita treeni pohjasta
  startWorkout: (programId: string, templateId: string, data?: StartWorkoutInput) =>
    fetchApi<Workout>(`/programs/${programId}/templates/${templateId}/start`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    }),
};

// ============================================
// Weight Tracking API (Painonseuranta)
// ============================================

export const weightApi = {
  // Hae painomittaukset
  list: (params?: { startDate?: string; endDate?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.startDate) searchParams.set('startDate', params.startDate);
    if (params?.endDate) searchParams.set('endDate', params.endDate);
    const query = searchParams.toString();
    return fetchApi<WeightEntry[]>(`/weight${query ? `?${query}` : ''}`);
  },

  // Hae yksittäinen mittaus
  get: (id: string) => fetchApi<WeightEntry>(`/weight/${id}`),

  // Lisää uusi mittaus
  create: (data: WeightEntryInput) =>
    fetchApi<WeightEntry>('/weight', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Päivitä mittaus
  update: (id: string, data: WeightUpdateInput) =>
    fetchApi<WeightEntry>(`/weight/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Poista mittaus
  delete: (id: string) =>
    fetchApi<{ success: boolean }>(`/weight/${id}`, { method: 'DELETE' }),

  // Hae tilastot ja keskiarvot
  getStats: (period: WeightPeriod = 'month') =>
    fetchApi<WeightStatsResponse>(`/weight/stats/summary?period=${period}`),
};

// ============================================
// User Profile API (Käyttäjäprofiili)
// ============================================

export const profileApi = {
  // Hae profiili
  get: () => fetchApi<UserProfile>('/profile'),

  // Päivitä profiili (luo jos ei ole)
  update: (data: ProfileUpdateInput) =>
    fetchApi<UserProfile>('/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Hae BMI
  getBmi: () => fetchApi<BMIResponse>('/profile/bmi'),
};

// ============================================
// Valmentaja (AI Coach)
// ============================================

export interface CoachSuggestion {
  type: 'progression' | 'balance' | 'frequency' | 'general';
  title: string;
  detail: string;
  exercise?: string | null;
}

export interface CoachResult {
  suggestions: CoachSuggestion[];
  generatedAt: string;
  note?: string;
  dataSummary: { workoutsAnalyzed: number; periodWeeks: number; exercisesTracked: number };
}

export const coachApi = {
  // On-demand tekoälyanalyysi treeneistä
  analyze: () => fetchApi<CoachResult>('/coach/analyze', { method: 'POST' }),
};

// ============================================
// Squat Program API (Kyykkyohjelma)
// ============================================

export const squatApi = {
  // Staattinen ohjelman määrittely
  getDefinition: () => fetchApi<{ weeks: SquatProgramWeekDef[] }>('/squat/definition'),

  // Viikon ennakkokatsaus painoineen
  getWeekPreview: (weekNumber: number, oneRepMax: number) =>
    fetchApi<{ week: SquatWeekWithWeights }>(
      `/squat/week/${weekNumber}/preview?oneRepMax=${oneRepMax}`
    ),

  // Aktiivinen ohjelma sessioineen
  getActive: () => fetchApi<{ program: SquatProgram | null }>('/squat/active'),

  // Käyttäjän ohjelmahistoria
  listPrograms: () => fetchApi<{ programs: SquatProgram[] }>('/squat/programs'),

  // Luo uusi ohjelma
  createProgram: (data: { oneRepMax: number; notes?: string }) =>
    fetchApi<{ program: SquatProgram }>('/squat/programs', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Aloita (tai jatka) viikon session
  startSession: (programId: string, weekNumber: number) =>
    fetchApi<{ session: SquatSession; resumed: boolean }>(
      `/squat/programs/${programId}/sessions`,
      {
        method: 'POST',
        body: JSON.stringify({ weekNumber }),
      }
    ),

  // Hae session
  getSession: (sessionId: string) =>
    fetchApi<{ session: SquatSession }>(`/squat/sessions/${sessionId}`),

  // Päivitä sarja (merkitse tehdyksi / hylätyksi)
  updateSet: (setId: string, data: { actualReps: number; completed: boolean }) =>
    fetchApi<{ set: SquatSet }>(`/squat/sets/${setId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // Päätä session (+ mahdollinen PR-päivitys viikolla 7)
  completeSession: (sessionId: string, data: { rpe?: number; notes?: string }) =>
    fetchApi<SquatCompleteResponse>(`/squat/sessions/${sessionId}/complete`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // Poista keskeneräinen session
  deleteSession: (sessionId: string) =>
    fetchApi<{ success: boolean }>(`/squat/sessions/${sessionId}`, {
      method: 'DELETE',
    }),

  // Ohjelman tilastot
  getStats: (programId: string) =>
    fetchApi<{ stats: SquatSessionStats[]; program: SquatProgram }>(
      `/squat/programs/${programId}/stats`
    ),

  // Hylkää aktiivinen ohjelma
  abandonProgram: (programId: string) =>
    fetchApi<{ program: SquatProgram }>(
      `/squat/programs/${programId}/abandon`,
      { method: 'PATCH', body: JSON.stringify({}) }
    ),
};
